import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  Badge,
  HStack,
  VStack,
  Text,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  IconButton,
  Flex,
  useToast,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { ChevronRightIcon, ViewIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { useSession, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";
import { useNavigate, Link } from "react-router-dom";

export default function MySessions() {
  const navigate = useNavigate();
  const toast = useToast();
  const session = useSession(api);
  const currentUser = session?.user;

  // Fetch available evaluation templates
  const [{ data: templatesData }] = useGlobalAction(
    api.getAvailableEvaluationTemplates,
    { userId: currentUser?.id },
    { pause: !currentUser }
  );

  // Create lookup map for template display names
  const templateDisplayNames = useMemo(() => {
    const map = {};
    templatesData?.templates?.forEach(t => {
      map[t.key] = t.displayName;
    });
    return map;
  }, [templatesData]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCohort, setSelectedCohort] = useState("");
  const [selectedFormType, setSelectedFormType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Fetch user's cohort memberships where they are an instructor
  const [{ data: userCohorts, fetching: fetchingCohorts, error: cohortsError }] = useFindMany(
    api.cohortMembership,
    {
      filter: {
        userId: { equals: currentUser?.id },
        primaryRole: { equals: "instructor" },
        active: { equals: true },
      },
      select: {
        id: true,
        cohort: {
          id: true,
          name: true,
          displayName: true,
        },
      },
    },
    { pause: !currentUser }
  );

  // Build cohort IDs list for filtering
  const instructorCohortIds = useMemo(() => {
    return userCohorts?.map((membership) => membership.cohort?.id).filter(Boolean) || [];
  }, [userCohorts]);

  // Build session filter
  const sessionFilter = useMemo(() => {
    if (!currentUser) return null;

    const filters = {
      sessionType: { equals: "standard_evaluation" },
    };

    // Instructor access: instructor.id = current user OR cohort in instructor cohorts OR (no cohort AND instructor = current user)
    const accessFilters = [
      { instructor: { id: { equals: currentUser.id } } },
    ];

    if (instructorCohortIds.length > 0) {
      accessFilters.push({
        cohort: { id: { in: instructorCohortIds } },
      });
    }

    filters.OR = accessFilters;

    // Apply additional filters
    if (selectedCohort) {
      if (selectedCohort === "no-cohort") {
        filters.cohort = { id: { isSet: false } };
      } else {
        filters.cohort = { id: { equals: selectedCohort } };
      }
    }

    if (selectedFormType) {
      filters.evaluationForm = { equals: selectedFormType };
    }

    if (dateFrom) {
      filters.startedAt = { greaterThanOrEqual: new Date(dateFrom).toISOString() };
    }

    if (dateTo) {
      const endDate = new Date(dateTo);
      endDate.setHours(23, 59, 59, 999);
      if (filters.startedAt) {
        filters.AND = [
          { startedAt: filters.startedAt },
          { startedAt: { lessThanOrEqual: endDate.toISOString() } },
        ];
        delete filters.startedAt;
      } else {
        filters.startedAt = { lessThanOrEqual: endDate.toISOString() };
      }
    }

    return filters;
  }, [currentUser, instructorCohortIds, selectedCohort, selectedFormType, dateFrom, dateTo]);

  // Fetch sessions
  const [{ data: sessions, fetching: fetchingSessions, error: sessionsError }] = useFindMany(
    api.simulationSession,
    {
      filter: sessionFilter,
      select: {
        id: true,
        sessionId: true,
        sessionType: true,
        startedAt: true,
        evaluationForm: true,
        status: true,
        scenario: {
          id: true,
          title: true,
          levelOfCare: true,
        },
        cohort: {
          id: true,
          name: true,
          displayName: true,
        },
        instructor: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
        scenarioShareToken: {
          token: true,
          expiresAt: true,
          isActive: true,
        },
      },
      sort: { startedAt: "Descending" },
    },
    { pause: !sessionFilter || fetchingCohorts }
  );

  // Fetch evaluation counts for ph4456
  const sessionIds = useMemo(() => sessions?.map((s) => s.id) || [], [sessions]);

  const [{ data: ph4456Evals, fetching: fetchingPh4456 }] = useFindMany(
    api.ph4456Evaluation,
    {
      filter: {
        simulationSession: { id: { in: sessionIds } },
      },
      select: {
        id: true,
        simulationSessionId: true,
      },
    },
    { pause: sessionIds.length === 0 }
  );

  const [{ data: nremtMedicalEvals, fetching: fetchingMedical }] = useFindMany(
    api.nremtMedicalEvaluation,
    {
      filter: {
        simulationSession: { id: { in: sessionIds } },
      },
      select: {
        id: true,
        simulationSessionId: true,
      },
    },
    { pause: sessionIds.length === 0 }
  );

  const [{ data: nremtTraumaEvals, fetching: fetchingTrauma }] = useFindMany(
    api.nremtTraumaEvaluation,
    {
      filter: {
        simulationSession: { id: { in: sessionIds } },
      },
      select: {
        id: true,
        simulationSessionId: true,
      },
    },
    { pause: sessionIds.length === 0 }
  );

  // Calculate evaluation counts per session
  const evaluationCounts = useMemo(() => {
    const counts = {};
    sessionIds.forEach((id) => {
      counts[id] = 0;
    });

    ph4456Evals?.forEach((evaluation) => {
      if (evaluation.simulationSessionId) {
        counts[evaluation.simulationSessionId] = (counts[evaluation.simulationSessionId] || 0) + 1;
      }
    });

    nremtMedicalEvals?.forEach((evaluation) => {
      if (evaluation.simulationSessionId) {
        counts[evaluation.simulationSessionId] = (counts[evaluation.simulationSessionId] || 0) + 1;
      }
    });

    nremtTraumaEvals?.forEach((evaluation) => {
      if (evaluation.simulationSessionId) {
        counts[evaluation.simulationSessionId] = (counts[evaluation.simulationSessionId] || 0) + 1;
      }
    });

    return counts;
  }, [sessionIds, ph4456Evals, nremtMedicalEvals, nremtTraumaEvals]);

  // Filter sessions by search term
  const filteredSessions = useMemo(() => {
    if (!sessions) return [];
    if (!searchTerm) return sessions;

    return sessions.filter((session) => {
      const scenarioTitle = session.scenario?.title?.toLowerCase() || "";
      const cohortName = session.cohort?.displayName?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();
      return scenarioTitle.includes(search) || cohortName.includes(search);
    });
  }, [sessions, searchTerm]);

  // Get unique cohorts for filter dropdown
  const availableCohorts = useMemo(() => {
    const cohorts = [];
    const seenIds = new Set();

    userCohorts?.forEach((membership) => {
      if (membership.cohort && !seenIds.has(membership.cohort.id)) {
        cohorts.push(membership.cohort);
        seenIds.add(membership.cohort.id);
      }
    });

    return cohorts;
  }, [userCohorts]);

  // Handle view session details
  const handleViewSession = (sessionId) => {
    navigate(`/my-scenarios/evaluations?sessionId=${sessionId}`);
  };

  const handleResumeSession = (sessionRecord) => {
    const token = sessionRecord?.scenarioShareToken?.token;
    if (!token) {
      toast({
        title: "Unable to resume",
        description: "No active share link token was found for this session.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    navigate(`/scenario-share/${encodeURIComponent(token)}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if token is expired
  const isTokenExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isLoading =
    fetchingCohorts || fetchingSessions || fetchingPh4456 || fetchingMedical || fetchingTrauma;

  if (cohortsError || sessionsError) {
    return (
      <Container maxW="container.xl" py={8} bg="gray.900" minH="100vh">
        <Text color="red.400" fontSize="lg">Error loading sessions: {cohortsError?.message || sessionsError?.message}</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8} bg="gray.900" minH="100vh">
      {/* Breadcrumbs */}
      <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.400" />} mb={4}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/my-scenarios" color="gray.300" _hover={{ color: "white" }}>
            My Scenarios
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink color="gray.100">My Sessions</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <HStack justifyContent="space-between" mb={6}>
        <Heading size="lg" color="white">My Evaluation Sessions</Heading>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="outline"
          colorScheme="whiteAlpha"
          onClick={() => navigate("/my-scenarios")}
        >
          Back to Scenarios
        </Button>
      </HStack>

      {/* Filters */}
      <Box bg="gray.800" p={4} borderRadius="md" shadow="sm" mb={6}>
        <VStack spacing={4} align="stretch">
          {/* Search bar */}
          <Input
            placeholder="Search by scenario or cohort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _placeholder={{ color: "gray.400" }}
            _hover={{ borderColor: "gray.500" }}
            _focus={{ borderColor: "blue.400", bg: "gray.700" }}
          />

          {/* Filter row */}
          <HStack spacing={4} flexWrap="wrap">
            {/* Date from */}
            <Box flex="1" minW="200px">
              <Text fontSize="sm" mb={1} color="gray.300">
                From Date
              </Text>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "blue.400", bg: "gray.700" }}
              />
            </Box>

            {/* Date to */}
            <Box flex="1" minW="200px">
              <Text fontSize="sm" mb={1} color="gray.300">
                To Date
              </Text>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "blue.400", bg: "gray.700" }}
              />
            </Box>

            {/* Cohort filter */}
            <Box flex="1" minW="200px">
              <Text fontSize="sm" mb={1} color="gray.300">
                Cohort
              </Text>
              <Select
                value={selectedCohort}
                onChange={(e) => setSelectedCohort(e.target.value)}
                placeholder="All Cohorts"
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "blue.400", bg: "gray.700" }}
              >
                <option value="no-cohort" style={{ background: '#2D3748', color: 'white' }}>No Cohort</option>
                {availableCohorts.map((cohort) => (
                  <option key={cohort.id} value={cohort.id} style={{ background: '#2D3748', color: 'white' }}>
                    {cohort.displayName || cohort.name}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Form type filter */}
            <Box flex="1" minW="200px">
              <Text fontSize="sm" mb={1} color="gray.300">
                Form Type
              </Text>
              <Select
                value={selectedFormType}
                onChange={(e) => setSelectedFormType(e.target.value)}
                placeholder="All Forms"
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "blue.400", bg: "gray.700" }}
              >
                {templatesData?.templates?.map((template) => (
                  <option key={template.key} value={template.key} style={{ background: '#2D3748', color: 'white' }}>
                    {template.displayName}
                  </option>
                ))}
              </Select>
            </Box>
          </HStack>

          {/* Clear filters */}
          {(searchTerm || selectedCohort || selectedFormType || dateFrom || dateTo) && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={() => {
                setSearchTerm("");
                setSelectedCohort("");
                setSelectedFormType("");
                setDateFrom("");
                setDateTo("");
              }}
            >
              Clear All Filters
            </Button>
          )}
        </VStack>
      </Box>

      {/* Sessions Table */}
      <Box bg="gray.800" borderRadius="md" shadow="sm" overflowX="auto">
        {isLoading ? (
          <Box p={8}>
            <VStack spacing={4}>
              <Skeleton height="40px" startColor="gray.700" endColor="gray.600" />
              <SkeletonText noOfLines={5} spacing={4} startColor="gray.700" endColor="gray.600" />
            </VStack>
          </Box>
        ) : filteredSessions.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text color="gray.300" fontSize="lg">
              No evaluation sessions found
            </Text>
            <Text color="gray.400" fontSize="md" mt={2}>
              {searchTerm || selectedCohort || selectedFormType || dateFrom || dateTo
                ? "Try adjusting your filters"
                : "Sessions will appear here once created"}
            </Text>
          </Box>
        ) : (
          <Table variant="simple" colorScheme="whiteAlpha">
            <Thead>
              <Tr>
                <Th color="gray.200" fontSize="md">Date</Th>
                <Th color="gray.200" fontSize="md">Scenario</Th>
                <Th color="gray.200" fontSize="md">Cohort</Th>
                <Th color="gray.200" fontSize="md">Form Type</Th>
                <Th color="gray.200" fontSize="md"># Evaluations</Th>
                <Th color="gray.200" fontSize="md">Session Owner</Th>
                <Th color="gray.200" fontSize="md">Status</Th>
                <Th color="gray.200" fontSize="md">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSessions.map((session) => {
                const evalCount = evaluationCounts[session.id] || 0;
                const tokenExpired = session.scenarioShareToken
                  ? isTokenExpired(session.scenarioShareToken.expiresAt)
                  : false;
                const isActive = session.scenarioShareToken?.isActive && !tokenExpired;

                return (
                  <Tr key={session.id} _hover={{ bg: "gray.700" }}>
                    <Td color="gray.100" fontSize="md">{formatDate(session.startedAt)}</Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" color="gray.100" fontSize="md">{session.scenario?.title || "Unknown"}</Text>
                        {session.scenario?.levelOfCare && (
                          <Text fontSize="sm" color="gray.400">
                            {session.scenario.levelOfCare}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      {session.cohort ? (
                        <Text color="gray.100" fontSize="md">{session.cohort.displayName || session.cohort.name}</Text>
                      ) : (
                        <Text color="gray.400" fontStyle="italic" fontSize="md">
                          No Cohort
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme="blue" fontSize="sm">
                        {templateDisplayNames[session.evaluationForm] || session.evaluationForm || "N/A"}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={evalCount > 0 ? "green" : "gray"} fontSize="sm">{evalCount}</Badge>
                    </Td>
                    <Td>
                      {session.instructor ? (
                        <Text color="gray.100" fontSize="md">
                          {session.instructor.firstName} {session.instructor.lastName}
                        </Text>
                      ) : (
                        <Text color="gray.400" fontSize="md">N/A</Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={isActive ? "green" : "red"} fontSize="sm">
                        {isActive ? "Active" : "Expired"}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<ViewIcon />}
                          aria-label="View session details"
                          size="sm"
                          onClick={() => handleViewSession(session.id)}
                          colorScheme="blue"
                          variant="ghost"
                        />
                        <Button
                          size="xs"
                          colorScheme="green"
                          variant={isActive ? "solid" : "outline"}
                          isDisabled={!isActive || !session?.scenarioShareToken?.token}
                          onClick={() => handleResumeSession(session)}
                        >
                          Resume
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Results count */}
      {!isLoading && filteredSessions.length > 0 && (
        <Text mt={4} fontSize="md" color="gray.300">
          Showing {filteredSessions.length} session{filteredSessions.length !== 1 ? "s" : ""}
        </Text>
      )}
    </Container>
  );
}
